import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import ProviderCard from "@/components/ProviderCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Search as SearchIcon, SlidersHorizontal, MapPin } from "lucide-react";

export default function Search() {
  const [params] = useSearchParams();
  const [providers, setProviders] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    zip: params.get("zip") || "",
    service: params.get("service") || "all",
    min_rating: 0,
    max_price: 500,
    sort: "recommended",
    q: "",
  });

  useEffect(() => { api.get("/config").then((r) => setCats(r.data.service_categories)).catch(() => {}); }, []);

  const run = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filters.zip) p.set("zip_code", filters.zip);
    if (filters.service && filters.service !== "all") p.set("service", filters.service);
    if (filters.min_rating) p.set("min_rating", filters.min_rating);
    if (filters.max_price < 500) p.set("max_price", filters.max_price);
    if (filters.q) p.set("q", filters.q);
    p.set("sort", filters.sort);
    try {
      const res = await api.get(`/providers/search?${p.toString()}`);
      setProviders(res.data.providers);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { run(); }, [run]);

  return (
    <Layout>
      <div className="hero-gradient grain text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
          <h1 className="font-display text-4xl font-extrabold">Find lawn care near you</h1>
          <p className="text-white/60 mt-2">Compare {providers.length} local providers by price, rating, and availability.</p>
          <div className="mt-6 bg-white rounded-2xl p-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 max-w-2xl">
            <div className="flex items-center gap-2 flex-1 pl-3">
              <MapPin className="w-5 h-5 text-muted-foreground" />
              <Input data-testid="search-zip" value={filters.zip} onChange={(e) => setFilters({ ...filters, zip: e.target.value })} placeholder="ZIP code" className="border-0 shadow-none focus-visible:ring-0 text-lawn-ink px-0" />
            </div>
            <div className="flex items-center gap-2 flex-1 pl-3 border-l border-border">
              <SearchIcon className="w-5 h-5 text-muted-foreground" />
              <Input data-testid="search-query" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Business name" className="border-0 shadow-none focus-visible:ring-0 text-lawn-ink px-0" />
            </div>
            <Button data-testid="search-run-btn" onClick={run} className="rounded-xl bg-lawn hover:bg-lawn-dark font-semibold px-6">Search</Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <div className="bg-white border border-border rounded-2xl p-6 sticky top-24">
            <div className="flex items-center gap-2 mb-5"><SlidersHorizontal className="w-4 h-4 text-lawn" /><h3 className="font-display font-bold text-lawn-ink">Filters</h3></div>
            <div className="space-y-5">
              <div>
                <Label className="text-xs">Service type</Label>
                <Select value={filters.service} onValueChange={(v) => setFilters({ ...filters, service: v })}>
                  <SelectTrigger data-testid="filter-service" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All services</SelectItem>
                    {cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Minimum rating: {filters.min_rating}★</Label>
                <Slider data-testid="filter-rating" value={[filters.min_rating]} onValueChange={([v]) => setFilters({ ...filters, min_rating: v })} min={0} max={5} step={0.5} className="mt-3" />
              </div>
              <div>
                <Label className="text-xs">Max price: ${filters.max_price}</Label>
                <Slider data-testid="filter-price" value={[filters.max_price]} onValueChange={([v]) => setFilters({ ...filters, max_price: v })} min={40} max={500} step={10} className="mt-3" />
              </div>
              <div>
                <Label className="text-xs">Sort by</Label>
                <Select value={filters.sort} onValueChange={(v) => setFilters({ ...filters, sort: v })}>
                  <SelectTrigger data-testid="filter-sort" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recommended">Recommended</SelectItem>
                    <SelectItem value="rating">Highest rated</SelectItem>
                    <SelectItem value="reviews">Most reviews</SelectItem>
                    <SelectItem value="price_low">Lowest price</SelectItem>
                    <SelectItem value="response">Fastest response</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </aside>

        <div className="lg:col-span-9">
          {loading ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">{[...Array(6)].map((_, i) => <div key={i} className="h-80 bg-white rounded-2xl border border-border animate-pulse" />)}</div>
          ) : providers.length === 0 ? (
            <div className="text-center py-24 bg-white rounded-2xl border border-border">
              <p className="font-display text-xl font-bold text-lawn-ink">No providers found</p>
              <p className="text-muted-foreground mt-2">Try adjusting your filters or ZIP code.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {providers.map((p, i) => <ProviderCard key={p.provider_id} provider={p} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
